/**
 * Design materialisation — the scaffolder's design engine.
 *
 * materializeTheme() bakes the derived palette/fonts/radius into the four
 * theme-BEARING files (index.css, tokens.json, index.html, tailwind.config.ts).
 * This module goes one layer up: it REGENERATES the design DOCUMENTATION the
 * frontend agents read (design-system/{index,guidelines,components,layouts}.json,
 * frontend/CLAUDE.md) so the docs describe the app's REAL theme — its actual
 * mode, fonts, and semantic utilities — instead of the template's placeholder
 * copy, and it adds a root DESIGN.md carrying the app's design brief.
 *
 * It also defines AppDesignSystem — the persisted, validated design-system
 * record a producer stores per app (vibe + palette inputs + font + radius +
 * tone + brief) and later re-derives a ScaffoldTheme from.
 *
 * Pure: same input → same output, no disk writes, sorted output — the same
 * contract as materializeTheme.
 */

import { z } from 'zod';
import { FONT_PAIRINGS } from '@ghosty/branding';
import type { ScaffoldFile } from './scaffold.js';
import {
  materializeTheme,
  radiusScale,
  type RadiusStyle,
  type ScaffoldTheme,
} from './theme.js';

export type { RadiusStyle };

/**
 * The persisted design system of ONE app — everything needed to re-derive its
 * ScaffoldTheme (mode/primary/secondary → derivePalette, fontKey → pairing,
 * radius → scale) plus the narrative the docs are generated from.
 */
export interface AppDesignSystem {
  version: 1;
  /** One-line feel the user asked for, e.g. 'warm, editorial, unhurried'. */
  vibe: string;
  mode: 'light' | 'dark';
  /** Primary brand colour, #rrggbb. */
  primary: string;
  /** Optional secondary brand colour, #rrggbb. */
  secondary?: string;
  /** One of the curated FONT_PAIRINGS keys. */
  fontKey: string;
  radius: RadiusStyle;
  /** 3–6 short adjectives that steer copy + component choices. */
  toneWords: string[];
  /** The full design brief (markdown) — becomes the app's DESIGN.md. */
  brief: string;
  updatedAt?: string;
  updatedBy?: string;
}

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/** The valid fontKey values — the curated pairing keys from @ghosty/branding. */
const FONT_KEYS = FONT_PAIRINGS.map((p) => p.key) as [string, ...string[]];

export const AppDesignSystemSchema: z.ZodType<AppDesignSystem> = z.object({
  version: z.literal(1),
  vibe: z.string().min(1).max(500),
  mode: z.enum(['light', 'dark']),
  primary: z.string().regex(HEX_COLOR, 'must be a #rrggbb hex colour'),
  secondary: z.string().regex(HEX_COLOR, 'must be a #rrggbb hex colour').optional(),
  fontKey: z.enum(FONT_KEYS),
  radius: z.enum(['sharp', 'rounded', 'pill']),
  toneWords: z.array(z.string().min(1).max(32)).min(3).max(6),
  brief: z.string().min(1).max(6000),
  updatedAt: z.string().optional(),
  updatedBy: z.string().optional(),
});

/** Per-app narrative inputs for the generated design docs. All optional except
 * the app name; absent vibe/brief → a generic house brief. */
export interface DesignDocMeta {
  appName: string;
  vibe?: string;
  toneWords?: string[];
  brief?: string;
}

/** Posix paths of the generated design docs, relative to the app root. */
const DS_DIR = 'frontend/src/design-system';
const INDEX_JSON = `${DS_DIR}/index.json`;
const GUIDELINES_JSON = `${DS_DIR}/guidelines.json`;
const COMPONENTS_JSON = `${DS_DIR}/components.json`;
const LAYOUTS_JSON = `${DS_DIR}/layouts.json`;
const FRONTEND_CLAUDE_MD = 'frontend/CLAUDE.md';
const DESIGN_MD = 'DESIGN.md';

/** 'Fraunces, Georgia, serif' → 'Fraunces'; '"IBM Plex Mono", …' → 'IBM Plex Mono'. */
function primaryFamily(stack: string): string {
  const first = stack.split(',')[0] ?? stack;
  return first.trim().replace(/^["']|["']$/g, '');
}

/** 'Warm & Friendly' → 'warm-friendly' (theme-name slug fragment). */
function slugWord(word: string): string {
  return (
    word
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'custom'
  );
}

/** Derived theme name, e.g. 'warm-light' — or 'custom-dark' without tone words. */
function themeName(theme: ScaffoldTheme, meta: DesignDocMeta): string {
  const tone = meta.toneWords?.[0];
  return `${tone ? slugWord(tone) : 'custom'}-${theme.tokens.mode}`;
}

/** 'calm, focused and unfussy' — prose list of the tone words (or a default). */
function tonePhrase(meta: DesignDocMeta): string {
  const words = meta.toneWords && meta.toneWords.length > 0 ? meta.toneWords : ['calm', 'focused', 'unfussy'];
  if (words.length === 1) return words[0];
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

const jsonFile = (path: string, doc: unknown): ScaffoldFile => ({
  path,
  content: `${JSON.stringify(doc, null, 2)}\n`,
  encoding: 'utf8',
});

function renderIndexJson(theme: ScaffoldTheme, meta: DesignDocMeta): ScaffoldFile {
  const display = primaryFamily(theme.fontDisplayStack);
  const mono = primaryFamily(theme.fontMonoStack);
  const mode = theme.tokens.mode;
  const doc = {
    $comment:
      "Manifest for this app's design system. Frontend agents (frontend-page / frontend-component) read this first, then the file it points at for the task at hand. Keep this catalog accurate when you add token files.",
    name: `${meta.appName} design system`,
    theme: themeName(theme, meta),
    version: 1,
    summary:
      `A ${tonePhrase(meta)} ${mode} theme: the page sits on --bg with cards on --surface/--surface-2, ` +
      `primary text in --text (secondary in --muted), and the brand accent --accent (readable text on it: --on-accent), ` +
      `set in ${display} with ${mono} for code. Minimal by design — a starter, not a full design system. ` +
      'Extend it by editing these files, not by inventing one-off styles.',
    readOrder: [
      '../../../DESIGN.md',
      'guidelines.json',
      'tokens.json',
      'components.json',
      'layouts.json',
    ],
    files: {
      '../../../DESIGN.md': "The app's design brief — the intent behind these tokens. Read it before any UI work.",
      'tokens.json':
        'Raw design tokens — colors, typography, spacing, radii, shadows, layout widths. The source of truth; mirrors the CSS variables in src/index.css.',
      'components.json':
        'Tailwind class recipes for common elements (button, card, input, badge, status dot, code, link), built from the tokens.',
      'layouts.json': 'Page scaffolding patterns (shell, centered, sidebar, list+detail).',
      'guidelines.json':
        'Hard constraints: never hardcode colors/spacing/typography, contrast rules, accessibility requirements.',
    },
    wiring: {
      $comment: 'How these tokens connect to the rendered app, so the catalog and the screen never drift.',
      cssVariables:
        'src/index.css defines :root CSS variables (--bg, --surface, --surface-2, --line, --text, --muted, --accent, --accent-hover, --on-accent, --secondary, --on-secondary, --success, --warning, --danger, --focus, --radius-sm/md/lg, --font-display, --font-mono) whose values equal tokens.json values.',
      tailwind:
        `tailwind.config.ts maps those CSS variables to utilities (bg-bg, bg-surface, bg-surface-2, text-text, text-muted, bg-accent, text-on-accent, border-line, rounded-md, …) and sets font-sans to ${display} and font-mono to ${mono} via the font vars, matching tokens.json typography.`,
      rule:
        'To restyle the app, change a value in BOTH tokens.json (the catalog agents read) and src/index.css (what the browser renders). They must stay equal.',
    },
  };
  return jsonFile(INDEX_JSON, doc);
}

function renderGuidelinesJson(theme: ScaffoldTheme): ScaffoldFile {
  const display = primaryFamily(theme.fontDisplayStack);
  const mono = primaryFamily(theme.fontMonoStack);
  const mode = theme.tokens.mode;
  const doc = {
    $comment:
      'Hard constraints for any UI generated in this app. These keep the app on-brand and accessible. Read them before writing JSX; treat them as non-negotiable.',
    rules: [
      'Never hardcode a color. Use the Tailwind utilities mapped to tokens (bg-bg, bg-surface, bg-surface-2, text-text, text-muted, text-accent, bg-accent, text-on-accent, border-line, text-danger). No hex, rgb(), or arbitrary color values like text-[#fff].',
      'Never hardcode spacing as raw pixels or arbitrary values (p-[13px]). Use the Tailwind spacing scale steps in tokens.json (p-4, gap-2, space-y-8, etc.).',
      `Never introduce a second font. The whole app is set in ${display} via font-sans; use font-mono (${mono}) only to set off code, timestamps, and machine output.`,
      'Never invent a second visual language. Reuse the recipes in components.json and the patterns in layouts.json. If something is missing, compose it from tokens and add it back to those files.',
      "Build pages by composing layouts.json + components.json, not bespoke markup. New pages should use the 'shell' pattern so they match the starter App.tsx.",
      'Handle the three data states explicitly for anything loaded from the backend: loading, error, loaded.',
      "Data comes from the backend via relative fetch('/api/...') calls. Never hardcode a host, and never call a third-party API directly from the browser.",
    ],
    contrast: {
      $comment: `WCAG AA targets on this ${mode} theme. The token pairings below are derived and pre-checked — stay within them.`,
      minRatioBodyText: 4.5,
      minRatioLargeText: 3.0,
      safePairings: [
        { fg: 'text', bg: 'bg', use: 'primary text on the page' },
        { fg: 'text', bg: 'surface', use: 'primary text on cards' },
        { fg: 'muted', bg: 'bg', use: 'secondary text (meets AA for normal text)' },
        { fg: 'accent', bg: 'bg', use: 'links/highlights and large text' },
        {
          fg: 'on-accent',
          bg: 'accent',
          use: 'text/icons on an accent fill (primary buttons) — on-accent is derived to stay readable on the accent, so always pair bg-accent with text-on-accent',
        },
        { fg: 'on-secondary', bg: 'secondary', use: 'text/icons on a secondary-colour fill' },
      ],
      avoid: [
        'accent text on surface-2 for small body copy (borderline) — reserve accent for emphasis and large text',
        'muted text on surface-2 for anything that must be read carefully',
        `text-text on an accent fill — on a ${mode} theme the accent may sit close to the text colour; text-on-accent is the pairing that is checked`,
      ],
    },
    accessibility: [
      'Every interactive element must show a visible focus ring (focus-visible:ring-2 focus-visible:ring-accent) — the component recipes already include it.',
      "Status conveyed by color (the header dot) must also be conveyed by text (e.g. 'API connected').",
      'Buttons and inputs need accessible labels; icon-only controls need aria-label.',
    ],
  };
  return jsonFile(GUIDELINES_JSON, doc);
}

function renderComponentsJson(): ScaffoldFile {
  const doc = {
    $comment:
      "Tailwind class recipes for common elements, built entirely from tokens.json (every color/spacing/radius here maps to a token). Copy a recipe's classes onto your element; do not substitute raw hex or arbitrary values. If you need a variant that isn't here, compose it from tokens and add it back to this file.",
    button: {
      base: 'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium tracking-tight transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50 disabled:pointer-events-none',
      variants: {
        primary: 'bg-accent text-on-accent hover:bg-accent-hover',
        secondary: 'bg-surface-2 text-text border border-line hover:bg-surface',
        ghost: 'text-muted hover:text-text hover:bg-surface-2',
        danger: 'bg-danger text-bg hover:opacity-90',
      },
      example:
        '<button class="inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium tracking-tight transition-colors bg-accent text-on-accent hover:bg-accent-hover">Save</button>',
    },
    card: {
      base: 'rounded-lg border border-line bg-surface p-6',
      header: 'text-sm font-medium uppercase tracking-wide text-muted',
      body: 'mt-4 text-text',
      example:
        '<section class="rounded-lg border border-line bg-surface p-6"><h3 class="text-sm font-medium uppercase tracking-wide text-muted">Title</h3><div class="mt-4 text-text">…</div></section>',
    },
    input: {
      base: 'w-full rounded-md border border-line bg-surface-2 px-3 py-2 text-sm text-text placeholder:text-muted focus:outline-none focus:border-accent',
      label: 'block text-sm font-medium text-text mb-1.5',
      hint: 'mt-1 text-xs text-muted',
      error: 'mt-1 text-xs text-danger',
      example:
        '<label class="block text-sm font-medium text-text mb-1.5">Email</label><input class="w-full rounded-md border border-line bg-surface-2 px-3 py-2 text-sm text-text placeholder:text-muted focus:outline-none focus:border-accent" />',
    },
    badge: {
      base: 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
      variants: {
        neutral: 'bg-surface-2 text-muted border border-line',
        accent: 'bg-surface-2 text-accent border border-line',
        danger: 'bg-surface-2 text-danger border border-line',
      },
      example:
        '<span class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium bg-surface-2 text-accent border border-line">active</span>',
    },
    statusDot: {
      $comment: 'The small connection/health indicator used in the app header.',
      base: 'h-2 w-2 rounded-full',
      variants: {
        ok: 'bg-accent',
        error: 'bg-danger',
        idle: 'bg-line',
      },
    },
    code: {
      $comment: 'Inline code / machine output. Set in the mono face (font-mono) and set off with a surface.',
      inline: 'rounded bg-surface-2 px-1.5 py-0.5 font-mono text-text',
    },
    link: {
      base: 'text-accent underline-offset-2 hover:underline',
    },
  };
  return jsonFile(COMPONENTS_JSON, doc);
}

function renderLayoutsJson(): ScaffoldFile {
  const doc = {
    $comment:
      "Page scaffolding patterns. Pick the one that fits what the page does, then fill it with components.json recipes. All widths/padding reference tokens.json layout values. The starter App.tsx uses the 'shell' pattern — match it for new pages so the app feels like one app.",
    shell: {
      $comment:
        'The default app frame: fixed header, scrolling main, footer. This is what App.tsx renders. Wrap every page in it.',
      container: 'min-h-screen flex flex-col',
      header: 'border-b border-line',
      headerInner: 'mx-auto max-w-3xl px-6 py-5 flex items-center justify-between',
      main: 'flex-1',
      mainInner: 'mx-auto max-w-3xl px-6 py-12 space-y-8',
      footer: 'border-t border-line',
      footerInner: 'mx-auto max-w-3xl px-6 py-4 text-xs text-muted',
    },
    centered: {
      $comment: 'Single focused thing in the middle of the screen — sign-in, empty state, a wizard step.',
      container: 'min-h-screen flex items-center justify-center px-6',
      panel: 'w-full max-w-md rounded-lg border border-line bg-surface p-8 space-y-6',
    },
    sidebar: {
      $comment: 'Persistent left nav + content area. Use the wide width for the content column.',
      container: 'min-h-screen flex',
      sidebar: 'w-60 shrink-0 border-r border-line p-4 space-y-1',
      navItem: 'block rounded-md px-3 py-2 text-sm text-muted hover:text-text hover:bg-surface-2',
      navItemActive: 'block rounded-md px-3 py-2 text-sm text-text bg-surface-2',
      content: 'flex-1 overflow-auto',
      contentInner: 'mx-auto max-w-6xl px-6 py-12 space-y-8',
    },
    listDetail: {
      $comment:
        "A scrollable list on the left, the selected item's detail on the right. Good for inboxes, records, queues.",
      container: 'min-h-screen flex',
      list: 'w-80 shrink-0 border-r border-line overflow-auto',
      listItem: 'block w-full text-left border-b border-line px-4 py-3 hover:bg-surface-2',
      listItemActive: 'block w-full text-left border-b border-line px-4 py-3 bg-surface-2',
      detail: 'flex-1 overflow-auto',
      detailInner: 'mx-auto max-w-3xl px-6 py-12 space-y-8',
    },
  };
  return jsonFile(LAYOUTS_JSON, doc);
}

function renderFrontendClaudeMd(theme: ScaffoldTheme): ScaffoldFile {
  const display = primaryFamily(theme.fontDisplayStack);
  const mono = primaryFamily(theme.fontMonoStack);
  const content = `# Frontend guidelines

This is the app's React 18 + Vite + TypeScript + Tailwind frontend. In
production the backend serves the built output; in local dev the Vite server
runs on :5173 and proxies \`/api\` to the backend.

The people you build for are non-technical. Talk about what they'll *see*,
never about components, props, or CSS.

## Read the design system before generating ANY UI

Before you write or change a single line of UI — a page, a component, even a
one-line tweak — read the app's design brief in \`DESIGN.md\` (repo root),
then \`src/design-system/\`. Start with \`index.json\` (the manifest), then
\`guidelines.json\`, then \`tokens.json\` and the file for your task:

| File | What it gives you |
|---|---|
| \`../DESIGN.md\` | The app's design brief — the intent behind the tokens |
| \`index.json\` | Manifest + how the tokens wire to the rendered app |
| \`guidelines.json\` | Hard constraints + contrast/accessibility rules |
| \`tokens.json\` | Raw tokens: colors, typography, spacing, radii, shadows |
| \`components.json\` | Tailwind class recipes for button, card, input, badge, … |
| \`layouts.json\` | Page patterns: shell, centered, sidebar, list+detail |

**Non-negotiable rules** (full list in \`guidelines.json\`):

- **Never hardcode colors.** Use the token-mapped Tailwind utilities:
  \`bg-bg\` (page), \`bg-surface\` / \`bg-surface-2\` (cards / nested),
  \`text-text\` (primary text), \`text-muted\` (secondary), \`text-accent\` /
  \`bg-accent\` with \`text-on-accent\` (highlights, primary action),
  \`border-line\` (borders), \`text-danger\` (errors). No hex, \`rgb()\`, or
  arbitrary values like \`text-[#fff]\`.
- **Never hardcode spacing** as raw pixels or arbitrary values (\`p-[13px]\`).
  Use the Tailwind scale steps in \`tokens.json\` (\`p-4\`, \`gap-2\`, \`space-y-8\`).
- **Never introduce a second font.** The whole app is set in ${display} via
  \`font-sans\`; use \`font-mono\` (${mono}) only to set off code/timestamps.
- **Compose from the recipes**, don't invent markup. If something's missing,
  build it from tokens and add it back to \`components.json\` / \`layouts.json\`
  so the next change reuses it.

The tokens mirror the CSS variables in \`src/index.css\`, which
\`tailwind.config.ts\` maps to the utilities above. To restyle the app, change
the value in BOTH \`tokens.json\` and \`index.css\` so the catalog and the screen
stay in sync.

## Pages and routing

- \`src/App.tsx\` is the app shell (the \`shell\` layout in \`layouts.json\`):
  header / \`main\` (\`max-w-3xl\` unless the layout genuinely needs more) /
  footer. Start here for UI changes; match this frame for new pages so the
  app feels like one app.
- One component per file in \`src/\`; PascalCase filenames. Extract a component
  when a section is reused or \`App.tsx\` grows past ~150 lines.
- This starter is a single page. If the app needs multiple routes, add
  \`react-router-dom\` (see the \`add-dependency\` skill) and keep route
  components under \`src/\`.

## Data

- Data comes from the backend via **relative** \`fetch('/api/...')\` calls. The
  Vite dev server proxies \`/api\` to the backend and production is same-origin,
  so never hardcode a host or \`localhost\`.
- Never call a third-party API directly from the browser — route it through
  the backend so keys stay server-side.
- Handle the three states explicitly for anything you load: loading, error,
  loaded.

## Verify

If dev servers are running (\`ghosty dev\`), confirm the page renders without
console errors, then describe the change to the user in terms of what they'll
see — not which files changed.
`;
  return { path: FRONTEND_CLAUDE_MD, content, encoding: 'utf8' };
}

function renderDesignMd(theme: ScaffoldTheme, meta: DesignDocMeta): ScaffoldFile {
  const display = primaryFamily(theme.fontDisplayStack);
  const mono = primaryFamily(theme.fontMonoStack);
  const mode = theme.tokens.mode;
  const radii = radiusScale(theme.radius);
  const vibeLine = meta.vibe ? `\n> ${meta.vibe}\n` : '';
  const body =
    meta.brief && meta.brief.trim().length > 0
      ? meta.brief.trim()
      : `This app uses the platform's house style: a ${mode} interface that feels
${tonePhrase(meta)}. It is set in ${display} (with ${mono} for code and
machine output) on a quiet layered palette — page, surface, and nested
surface — with one brand accent doing all the emphasis, and
${radii.md} corners on controls and cards.

Keep it that way:

- Let whitespace and type hierarchy do the work; decoration is a last resort.
- One accent. Status colours (success/warning/danger) only for status.
- Borders over shadows for separation; shadows only for true overlays.
- Every visual value comes from the design tokens — never a raw hex, pixel
  value, or ad-hoc font.`;
  const content = `# ${meta.appName} — design brief
${vibeLine}
${body}

---

*How this brief becomes pixels:* the concrete values live in
\`frontend/src/design-system/\` (start with \`index.json\`, then
\`guidelines.json\` and \`tokens.json\`). Read this brief first, then those
files, before any UI work.
`;
  return { path: DESIGN_MD, content, encoding: 'utf8' };
}

/**
 * Render the six design documents for an app: the four design-system JSON
 * docs, the frontend CLAUDE.md, and the root DESIGN.md brief — all phrased
 * for the app's REAL theme (mode, fonts, radius) and using only the semantic
 * token utilities.
 */
export function renderDesignDocs(theme: ScaffoldTheme, meta: DesignDocMeta): ScaffoldFile[] {
  return [
    renderIndexJson(theme, meta),
    renderGuidelinesJson(theme),
    renderComponentsJson(),
    renderLayoutsJson(),
    renderFrontendClaudeMd(theme),
    renderDesignMd(theme, meta),
  ];
}

/**
 * Full design post-pass: materializeTheme (index.css / tokens.json /
 * index.html / tailwind.config.ts) PLUS regenerated design docs — existing
 * doc files are replaced, missing ones (DESIGN.md) are added. Pure — returns
 * a NEW sorted list, never mutates the input or writes to disk. Same contract
 * as materializeTheme.
 */
export function materializeDesign(
  files: ScaffoldFile[],
  theme: ScaffoldTheme,
  meta: DesignDocMeta,
): ScaffoldFile[] {
  const themed = materializeTheme(files, theme);
  const docs = new Map(renderDesignDocs(theme, meta).map((d) => [d.path, d]));
  const out = themed.map((f) => {
    const doc = docs.get(f.path);
    if (doc) docs.delete(f.path);
    return doc ?? f;
  });
  // Docs not present in the scaffold (the root DESIGN.md today) are added.
  out.push(...docs.values());
  return out.sort((a, b) => a.path.localeCompare(b.path));
}
